/**
 * @file formProductInputFieldes
 * 商品情報画面 項目名
 */

import {ProductInputText} from '@/js/util/constants'
import * as formProductInputOptionConstants from '@/js/util/member/formProductInputOptionConstants'

/**
 * 商品情報入力画面情報
 * @param {string} category 申込種別 (credit or installment)
 * @param {object} formState 商品情報の値
 * @param {date} appDate // 申込日
 * @param {array} finances // 金融商品一覧
 * @param {object} option // 店舗オプション
 */
export const FormProductInputFieldes = (category, formState, appDate, finances, option) => {
  return {
    // 商品
    product: {
      key: 'product',
      label: ProductInputText.product,
      isShow: category === 'installment'
    },
    // 商品その他
    productOtherName: {
      key: 'product_other_name',
      label: ProductInputText.product_other_name,
      isShow: true
    },
    // 役務提供開始
    serviceStartDate: {
      key: 'service_start_date',
      label: ProductInputText.service_start_date,
      isShow: category === 'installment'
    },
    // 役務提供終了
    serviceEndDate: {
      key: 'service_end_date',
      label: ProductInputText.service_end_date,
      isShow: category === 'installment'
    },
    // 現金価格合計
    cashTotal: {
      key: 'cash_total',
      label: ProductInputText.cash_total,
      isShow: true
    },

    // 頭金
    downPayment: {
      key: 'down_payment',
      label: ProductInputText.down_payment,
      isShow: true
    },

    // 残金（申し込み金額）
    remainingMoney: {
      key: 'remaining_money',
      label: ProductInputText.remaining_money,
      isShow: true
    },

    // 支払方法
    payment: {
      key: 'payment',
      label: ProductInputText.payment,
      isShow: true,
      choices: formProductInputOptionConstants.Payment(category, finances, formState.finance_category_name)
    },

    // 分割回数
    splitNumber: {
      key: 'split_number',
      label: ProductInputText.split_number,
      isShow: formState.payment === 'split' || formState.payment === 'split_bonus',
      choices: formProductInputOptionConstants.SplitNumber().filter((item) => {
        return (Number(item.value) >= option.split_lower_limit ?? 3) && (Number(item.value) <= option.split_limit ?? 60)
      })
    },

    // ボーナス併用加算額
    bonusCombinedAdditionAmount: {
      key: 'bonus_combined_addition_amount',
      label: ProductInputText.bonus_combined_addition_amount,
      isShow: formState.payment === 'split_bonus'
    },
    // ボーナス加算月
    bonusCombinedMonth: {
      key: 'bonus_combined_month',
      label: ProductInputText.bonus_combined_month,
      isShow: formState.payment === 'split_bonus',
      bonusCombinedMonth1: {
        key: 'bonus_combined_month1',
        label: '夏',
        choices: formProductInputOptionConstants.BonusCombinedMonth.summer
      },
      bonusCombinedMonth2: {
        key: 'bonus_combined_month2',
        label: '冬',
        choices: formProductInputOptionConstants.BonusCombinedMonth.winter
      }
    },
    // ボーナス加算回数
    bonusCombinedNumber: {
      key: 'bonus_combined_number',
      label: ProductInputText.bonus_combined_number,
      isShow: formState.payment === 'split_bonus'
    },
    // 支払月1回目
    bonusOnceMonth: {
      key: 'bonus_once_month',
      label: ProductInputText.bonus_once_month,
      isShow: formState.payment === 'once' || formState.payment === 'seconds',
      choices: formProductInputOptionConstants.BonusOnceMonth(formState.payment, appDate),
      fixChoices: formProductInputOptionConstants.FixBonusOnceMonth(
        formState.payment,
        category,
        appDate,
        option.setting_bonus_start_period,
        option.use_new_payment_start_rule
      )
    },
    // ボーナス二回の第2回目支払月
    bonusTwiceMonth: {
      key: 'bonus_twice_month',
      label: ProductInputText.bonus_twice_month,
      isShow: formState.payment === 'seconds',
      choices: formProductInputOptionConstants.BonusTwiceMonth(formState.bonus_once_month, appDate),
      fixChoices: formProductInputOptionConstants.FixBonusTwiceMonth(formState.bonus_once_month, category, appDate, option.setting_bonus_start_period, option.use_new_payment_start_rule)
    },
    // 支払期間 開始
    paymentPeriodStart: {
      key: 'payment_period_start',
      label: ProductInputText.payment_period_start,
      isShow: true
    },
    // 支払期間 開始(年)
    paymentPeriodStartY: {
      key: 'payment_period_start_y',
      label: ProductInputText.payment_period_start_y,
      isShow: true
    },
    // 支払期間 開始(月)
    paymentPeriodStartM: {
      key: 'payment_period_start_m',
      label: ProductInputText.payment_period_start_m,
      isShow: true,
      choices: formProductInputOptionConstants.allMonth()
    },
    // 支払期間 終了
    paymentPeriodEnd: {
      key: 'payment_period_end',
      label: ProductInputText.payment_period_end,
      isShow: true
    },
    // 支払金額 第1回目
    paymentAmountOnce: {
      key: 'payment_amount_once',
      label: ProductInputText.payment_amount_once,
      isShow: true
    },
    // 支払金額 第2回目以降
    paymentAmountSecond: {
      key: 'payment_amount_second',
      label: ProductInputText.payment_amount_second,
      isShow: formState.payment !== 'once' && formState.payment !== 'monthly'
    },
    // 分割手数料
    splitFee: {
      key: 'split_fee',
      label: ProductInputText.split_fee,
      isShow: formState.payment === 'split' || formState.payment === 'split_bonus'
    },
    // 一括二括手数料
    bonusFee: {
      key: 'bonus_fee',
      label: ProductInputText.bonus_fee,
      isShow: (formState.payment === 'once' || formState.payment === 'seconds') && Number(formState.split_fee) !== 0
    },
    // 分割支払合計
    splitTotalPayment: {
      key: 'split_total_payment',
      label: ProductInputText.split_total_payment,
      isShow: formState.payment !== 'monthly'
    },
    // 支払総額
    totalPay: {
      key: 'total_pay',
      label: ProductInputText.total_pay,
      isShow: true
    },
    // 担当者
    personCharge: {
      key: 'person_charge',
      label: ProductInputText.person_charge,
      isShow: true
    }
  }
}
